import { normalizeCurveUnit } from "./aiApply";
import { pixelPointToSource } from "./geometry";

import type {
  AxisQuantity,
  CurveDraftDocument,
  CurveKind,
  CurveSetPublicationInput,
  CurveSetRevisionRead,
  CurveTraceDraft,
} from "./types";

export interface PublicationDraft {
  affinityRows: Array<{ sourceTraceId: string; targetSpeedRpm: number }>;
  bepFlow: string;
  catalogRevisionId: string;
  catalogTemplateId: string;
  catalogVariantId: string;
  curveSetId: string | null;
  id: string;
  impellerDiameter: string;
  linkCatalog: boolean;
  maximumFlow: string;
  maximumHead: string;
  maximumSuctionLift: string;
  mcfsFlow: string;
  measuredTraceIds: string[];
  minimumFlow: string;
  minimumHead: string;
  minimumSuctionPressure: string;
  name: string;
  revisionLabel: string;
  testedSpeed: string;
}

export function createPublicationDraft(
  name: string,
  revisionLabel: string,
  testedSpeedRpm: number | null,
  measuredTraceIds: string[],
): PublicationDraft {
  return {
    affinityRows: [],
    bepFlow: "",
    catalogRevisionId: "",
    catalogTemplateId: "",
    catalogVariantId: "",
    curveSetId: null,
    id: globalThis.crypto.randomUUID(),
    impellerDiameter: "",
    linkCatalog: false,
    maximumFlow: "",
    maximumHead: "",
    maximumSuctionLift: "",
    mcfsFlow: "",
    measuredTraceIds,
    minimumFlow: "",
    minimumHead: "",
    minimumSuctionPressure: "",
    name,
    revisionLabel,
    testedSpeed: testedSpeedRpm === null ? "" : String(testedSpeedRpm),
  };
}

function optionalNumber(value: string): number | null {
  return value.trim() === "" ? null : Number(value);
}

export function publicationPayload(
  draft: PublicationDraft,
): CurveSetPublicationInput {
  return {
    affinity_derivations: draft.affinityRows.map((row) => ({
      label: null,
      source_trace_id: row.sourceTraceId,
      target_speed_rpm: row.targetSpeedRpm,
    })),
    catalog_target: draft.linkCatalog
      ? {
          pump_template_id: draft.catalogTemplateId,
          pump_template_revision_id: draft.catalogRevisionId,
          pump_variant_id: draft.catalogVariantId,
        }
      : null,
    curve_set_id: draft.curveSetId,
    impeller_diameter_m: optionalNumber(draft.impellerDiameter),
    measured_trace_ids: draft.measuredTraceIds,
    name: draft.name,
    operating_envelope: {
      bep_flow_m3_s: optionalNumber(draft.bepFlow),
      maximum_flow_m3_s: optionalNumber(draft.maximumFlow),
      maximum_head_m: optionalNumber(draft.maximumHead),
      minimum_continuous_stable_flow_m3_s: optionalNumber(draft.mcfsFlow),
      minimum_flow_m3_s: optionalNumber(draft.minimumFlow),
      minimum_head_m: optionalNumber(draft.minimumHead),
    },
    revision_label: draft.revisionLabel,
    suction_limits: {
      maximum_suction_lift_m: optionalNumber(draft.maximumSuctionLift),
      minimum_suction_pressure_absolute_pa: optionalNumber(
        draft.minimumSuctionPressure,
      ),
    },
    tested_speed_rpm: Number(draft.testedSpeed),
  };
}

function optionalNumberIsValid(
  value: string,
  minimum: number,
  exclusive = false,
): boolean {
  if (value.trim() === "") return true;
  const parsed = Number(value);
  return (
    Number.isFinite(parsed) &&
    (exclusive ? parsed > minimum : parsed >= minimum)
  );
}

function orderedOptionalNumbers(values: string[]): boolean {
  const present = values
    .filter((value) => value.trim() !== "")
    .map((value) => Number(value));
  return present.every(
    (value, index) => index === 0 || (present[index - 1] ?? value) <= value,
  );
}

export function publicationReady(draft: PublicationDraft): boolean {
  const testedSpeed = Number(draft.testedSpeed);
  const affinityIdentities = draft.affinityRows.map(
    (row) => row.sourceTraceId + ":" + row.targetSpeedRpm,
  );
  const affinityRowsAreValid = draft.affinityRows.every(
    (row) =>
      draft.measuredTraceIds.includes(row.sourceTraceId) &&
      Number.isInteger(row.targetSpeedRpm) &&
      row.targetSpeedRpm > 0,
  );
  const optionalValuesAreValid =
    optionalNumberIsValid(draft.impellerDiameter, 0, true) &&
    optionalNumberIsValid(draft.minimumFlow, 0) &&
    optionalNumberIsValid(draft.mcfsFlow, 0) &&
    optionalNumberIsValid(draft.bepFlow, 0) &&
    optionalNumberIsValid(draft.maximumFlow, 0, true) &&
    optionalNumberIsValid(draft.minimumHead, 0) &&
    optionalNumberIsValid(draft.maximumHead, 0, true) &&
    optionalNumberIsValid(draft.maximumSuctionLift, 0) &&
    optionalNumberIsValid(draft.minimumSuctionPressure, 0) &&
    orderedOptionalNumbers([
      draft.minimumFlow,
      draft.mcfsFlow,
      draft.bepFlow,
      draft.maximumFlow,
    ]) &&
    orderedOptionalNumbers([draft.minimumHead, draft.maximumHead]);

  return (
    draft.name.trim().length > 0 &&
    draft.name.trim().length <= 200 &&
    draft.revisionLabel.trim().length > 0 &&
    draft.revisionLabel.trim().length <= 100 &&
    draft.testedSpeed.trim() !== "" &&
    Number.isInteger(testedSpeed) &&
    testedSpeed > 0 &&
    draft.measuredTraceIds.length > 0 &&
    new Set(draft.measuredTraceIds).size === draft.measuredTraceIds.length &&
    affinityRowsAreValid &&
    new Set(affinityIdentities).size === affinityIdentities.length &&
    optionalValuesAreValid &&
    (!draft.linkCatalog ||
      (draft.catalogTemplateId !== "" &&
        draft.catalogRevisionId !== "" &&
        draft.catalogVariantId !== ""))
  );
}

const DIRECT_CURVE_KINDS = new Set<CurveKind>([
  "head",
  "efficiency",
  "brake_power",
  "npshr",
  "suction_lift",
]);

const AFFINITY_UNSUPPORTED_KINDS = new Set<CurveKind>([
  "npshr",
  "suction_lift",
]);

export function curveTraceSupportsAffinity(trace: CurveTraceDraft): boolean {
  return (
    trace.speed_rpm !== null &&
    trace.speed_rpm > 0 &&
    !AFFINITY_UNSUPPORTED_KINDS.has(trace.kind)
  );
}

function sourceValueToSi(
  quantity: "flow" | "head",
  value: number,
  unit: string,
): number | null {
  const normalized = normalizeCurveUnit(unit);
  const factor =
    quantity === "flow"
      ? {
          bpm: 0.158987294928 / 60,
          gpm: 0.003785411784 / 60,
          "l/s": 0.001,
          "m3/h": 1 / 3600,
          "m3/s": 1,
        }[normalized]
      : { ft: 0.3048, m: 1 }[normalized];
  return factor === undefined || !Number.isFinite(value)
    ? null
    : value * factor;
}

function headTraceDomains(
  trace: CurveTraceDraft,
  document: CurveDraftDocument,
): {
  maximumFlow: number;
  maximumHead: number;
  minimumFlow: number;
  minimumHead: number;
} | null {
  const calibration = trace.calibration_override ?? document.calibration;
  if (
    calibration === null ||
    calibration.x_axis.quantity !== "flow" ||
    calibration.y_axis.quantity !== "head"
  ) {
    return null;
  }
  const points = trace.points.map((point) => {
    const source =
      point.source ??
      (point.pixel === null
        ? null
        : pixelPointToSource(calibration, point.pixel));
    if (source === null) return null;
    const flow = sourceValueToSi("flow", source.x, calibration.x_axis.unit);
    const head = sourceValueToSi("head", source.y, calibration.y_axis.unit);
    return flow === null || head === null ? null : { flow, head };
  });
  if (points.length < 2 || points.some((point) => point === null)) return null;
  const completePoints = points.filter(
    (point): point is { flow: number; head: number } => point !== null,
  );
  return {
    maximumFlow: Math.max(...completePoints.map((point) => point.flow)),
    maximumHead: Math.max(...completePoints.map((point) => point.head)),
    minimumFlow: Math.min(...completePoints.map((point) => point.flow)),
    minimumHead: Math.min(...completePoints.map((point) => point.head)),
  };
}

function optionalValueWithin(
  value: string,
  minimum: number,
  maximum: number,
): boolean {
  if (value.trim() === "") return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum && parsed <= maximum;
}

export function publicationMatchesDocument(
  draft: PublicationDraft,
  document: CurveDraftDocument,
): boolean {
  const tracesById = new Map(document.traces.map((trace) => [trace.id, trace]));
  const selected = draft.measuredTraceIds.map((traceId) =>
    tracesById.get(traceId),
  );
  if (selected.some((trace) => trace === undefined)) return false;
  const selectedTraces = selected.filter(
    (trace): trace is CurveTraceDraft => trace !== undefined,
  );
  const headTraces = selectedTraces.filter((trace) => trace.kind === "head");
  if (headTraces.length !== 1) return false;

  const directKinds = selectedTraces
    .filter((trace) => DIRECT_CURVE_KINDS.has(trace.kind))
    .map((trace) => trace.kind);
  if (new Set(directKinds).size !== directKinds.length) return false;

  const testedSpeed = Number(draft.testedSpeed);
  if (
    selectedTraces.some(
      (trace) => trace.speed_rpm !== null && trace.speed_rpm !== testedSpeed,
    )
  ) {
    return false;
  }
  if (
    draft.affinityRows.some((row) => {
      const source = tracesById.get(row.sourceTraceId);
      return (
        source === undefined ||
        !draft.measuredTraceIds.includes(row.sourceTraceId) ||
        !curveTraceSupportsAffinity(source)
      );
    })
  ) {
    return false;
  }

  const domains = headTraceDomains(headTraces[0]!, document);
  return (
    domains !== null &&
    optionalValueWithin(
      draft.minimumFlow,
      domains.minimumFlow,
      domains.maximumFlow,
    ) &&
    optionalValueWithin(
      draft.mcfsFlow,
      domains.minimumFlow,
      domains.maximumFlow,
    ) &&
    optionalValueWithin(
      draft.bepFlow,
      domains.minimumFlow,
      domains.maximumFlow,
    ) &&
    optionalValueWithin(
      draft.maximumFlow,
      domains.minimumFlow,
      domains.maximumFlow,
    ) &&
    optionalValueWithin(
      draft.minimumHead,
      domains.minimumHead,
      domains.maximumHead,
    ) &&
    optionalValueWithin(
      draft.maximumHead,
      domains.minimumHead,
      domains.maximumHead,
    )
  );
}

const ENGINEERING_Y_QUANTITY: Record<CurveKind, AxisQuantity> = {
  brake_power: "power",
  brake_power_contour: "head",
  efficiency: "efficiency",
  efficiency_contour: "head",
  head: "head",
  npshr: "npshr",
  npshr_contour: "head",
  operating_boundary: "head",
  suction_lift: "suction_lift",
};

const ENGINEERING_UNIT_FACTORS: Record<
  AxisQuantity,
  Readonly<Record<string, number>>
> = {
  efficiency: { fraction: 1, percent: 0.01 },
  flow: {
    bpm: 0.158987294928 / 60,
    gpm: 0.003785411784 / 60,
    "l/s": 0.001,
    "m3/h": 1 / 3600,
    "m3/s": 1,
  },
  head: { ft: 0.3048, m: 1 },
  npshr: { ft: 0.3048, m: 1 },
  power: { hp: 745.6998715822702, kw: 1000, w: 1 },
  suction_lift: { ft: 0.3048, m: 1 },
};

interface EngineeringTraceSnapshot {
  affinitySpeedRatio: number | null;
  contourValueSi: number | null;
  kind: CurveKind;
  label: string;
  origin: "affinity_derived" | "contour_derived" | "measured" | "library_copy";
  points: Array<{ xSi: number; ySi: number }>;
  speedRpm: number | null;
  trimLabel: string | null;
  xQuantity: AxisQuantity;
  yQuantity: AxisQuantity;
}

function engineeringValueToSi(
  quantity: AxisQuantity,
  value: number,
  unit: string,
): number | null {
  if (!Number.isFinite(value)) return null;
  const normalized = normalizeCurveUnit(unit);
  const factor = ENGINEERING_UNIT_FACTORS[quantity][normalized];
  return factor === undefined ? null : value * factor;
}

function measuredEngineeringSnapshot(
  trace: CurveTraceDraft,
  document: CurveDraftDocument,
): EngineeringTraceSnapshot | null {
  const calibration = trace.calibration_override ?? document.calibration;
  const yQuantity = ENGINEERING_Y_QUANTITY[trace.kind];
  if (
    calibration === null ||
    calibration.x_axis.quantity !== "flow" ||
    calibration.y_axis.quantity !== yQuantity
  ) {
    return null;
  }

  let contourValueSi: number | null = null;
  if (
    trace.kind === "efficiency_contour" ||
    trace.kind === "brake_power_contour" ||
    trace.kind === "npshr_contour"
  ) {
    const contourQuantity =
      trace.kind === "efficiency_contour"
        ? "efficiency"
        : trace.kind === "brake_power_contour"
          ? "power"
          : "npshr";
    if (trace.contour_value === null || trace.contour_unit === null)
      return null;
    contourValueSi = engineeringValueToSi(
      contourQuantity,
      trace.contour_value,
      trace.contour_unit,
    );
    if (contourValueSi === null) return null;
  }

  const points: Array<{ sequence: number; xSi: number; ySi: number }> = [];
  for (const point of trace.points) {
    const source =
      point.source ??
      (point.pixel === null
        ? null
        : pixelPointToSource(calibration, point.pixel));
    if (source === null) return null;
    const xSi = engineeringValueToSi("flow", source.x, calibration.x_axis.unit);
    const ySi = engineeringValueToSi(
      yQuantity,
      source.y,
      calibration.y_axis.unit,
    );
    if (xSi === null || ySi === null) return null;
    points.push({ sequence: point.sequence, xSi, ySi });
  }
  points.sort(
    (left, right) => left.xSi - right.xSi || left.sequence - right.sequence,
  );

  return {
    affinitySpeedRatio: null,
    contourValueSi,
    kind: trace.kind,
    label: trace.label,
    origin: trace.origin,
    points: points.map(({ xSi, ySi }) => ({ xSi, ySi })),
    speedRpm: trace.speed_rpm,
    trimLabel: trace.trim_label,
    xQuantity: "flow",
    yQuantity,
  };
}

function savedEngineeringSnapshot(
  trace: CurveSetRevisionRead["measured_traces"][number],
): EngineeringTraceSnapshot {
  return {
    affinitySpeedRatio: trace.affinity_speed_ratio ?? null,
    contourValueSi: trace.contour_value_si ?? null,
    kind: trace.kind,
    label: trace.label,
    origin: trace.origin,
    points: trace.points.map((point) => ({
      xSi: point.x_si,
      ySi: point.y_si,
    })),
    speedRpm: trace.speed_rpm ?? null,
    trimLabel: trace.trim_label ?? null,
    xQuantity: trace.x_quantity,
    yQuantity: trace.y_quantity,
  };
}

function affinityEngineeringSnapshot(
  source: EngineeringTraceSnapshot,
  targetSpeedRpm: number,
): EngineeringTraceSnapshot | null {
  if (source.speedRpm === null || source.speedRpm <= 0) return null;
  const ratio = targetSpeedRpm / source.speedRpm;
  const yRatio =
    source.yQuantity === "head"
      ? ratio ** 2
      : source.yQuantity === "power"
        ? ratio ** 3
        : source.yQuantity === "efficiency"
          ? 1
          : null;
  if (yRatio === null) return null;
  return {
    ...source,
    affinitySpeedRatio: ratio,
    contourValueSi:
      source.kind === "brake_power_contour" && source.contourValueSi !== null
        ? source.contourValueSi * ratio ** 3
        : source.contourValueSi,
    label: `${source.label} affinity-derived at ${targetSpeedRpm} RPM`,
    origin: "affinity_derived",
    points: source.points.map((point) => ({
      xSi: point.xSi * ratio,
      ySi: point.ySi * yRatio,
    })),
    speedRpm: targetSpeedRpm,
  };
}

function currentEngineeringPayload(
  draft: PublicationDraft,
  document: CurveDraftDocument,
) {
  const tracesById = new Map(document.traces.map((trace) => [trace.id, trace]));
  const snapshotsById = new Map<string, EngineeringTraceSnapshot>();
  const measuredTraces: EngineeringTraceSnapshot[] = [];
  for (const traceId of draft.measuredTraceIds) {
    const trace = tracesById.get(traceId);
    if (trace === undefined) return null;
    const snapshot = measuredEngineeringSnapshot(trace, document);
    if (snapshot === null) return null;
    snapshotsById.set(traceId, snapshot);
    measuredTraces.push(snapshot);
  }
  const affinityDerivedTraces: EngineeringTraceSnapshot[] = [];
  for (const row of draft.affinityRows) {
    const source = snapshotsById.get(row.sourceTraceId);
    if (source === undefined) return null;
    const derived = affinityEngineeringSnapshot(source, row.targetSpeedRpm);
    if (derived === null) return null;
    affinityDerivedTraces.push(derived);
  }

  return {
    affinityDerivedTraces,
    impellerDiameter: optionalNumber(draft.impellerDiameter),
    measuredTraces,
    operatingEnvelope: {
      bepFlow: optionalNumber(draft.bepFlow),
      maximumFlow: optionalNumber(draft.maximumFlow),
      maximumHead: optionalNumber(draft.maximumHead),
      mcfsFlow: optionalNumber(draft.mcfsFlow),
      minimumFlow: optionalNumber(draft.minimumFlow),
      minimumHead: optionalNumber(draft.minimumHead),
    },
    suctionLimits: {
      maximumSuctionLift: optionalNumber(draft.maximumSuctionLift),
      minimumSuctionPressure: optionalNumber(draft.minimumSuctionPressure),
    },
    testedSpeedRpm: Number(draft.testedSpeed),
  };
}

function savedEngineeringPayload(revision: CurveSetRevisionRead) {
  return {
    affinityDerivedTraces: revision.affinity_derived_traces.map(
      savedEngineeringSnapshot,
    ),
    impellerDiameter: revision.impeller_diameter_m ?? null,
    measuredTraces: revision.measured_traces.map(savedEngineeringSnapshot),
    operatingEnvelope: {
      bepFlow: revision.operating_envelope.bep_flow_m3_s ?? null,
      maximumFlow: revision.operating_envelope.maximum_flow_m3_s ?? null,
      maximumHead: revision.operating_envelope.maximum_head_m ?? null,
      mcfsFlow:
        revision.operating_envelope.minimum_continuous_stable_flow_m3_s ?? null,
      minimumFlow: revision.operating_envelope.minimum_flow_m3_s ?? null,
      minimumHead: revision.operating_envelope.minimum_head_m ?? null,
    },
    suctionLimits: {
      maximumSuctionLift:
        revision.suction_limits.maximum_suction_lift_m ?? null,
      minimumSuctionPressure:
        revision.suction_limits.minimum_suction_pressure_absolute_pa ?? null,
    },
    testedSpeedRpm: revision.tested_speed_rpm,
  };
}

export function publicationEngineeringChanged(
  draft: PublicationDraft,
  document: CurveDraftDocument,
  priorRevision: CurveSetRevisionRead,
): boolean {
  const current = currentEngineeringPayload(draft, document);
  if (current === null) return true;
  return !engineeringValuesEqual(
    current,
    savedEngineeringPayload(priorRevision),
  );
}

function engineeringValuesEqual(left: unknown, right: unknown): boolean {
  if (typeof left === "number" && typeof right === "number") {
    const tolerance = 1e-12 * Math.max(1, Math.abs(left), Math.abs(right));
    return Math.abs(left - right) <= tolerance;
  }
  if (
    left === null ||
    right === null ||
    typeof left !== "object" ||
    typeof right !== "object"
  ) {
    return left === right;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => engineeringValuesEqual(value, right[index]))
    );
  }
  const leftRecord = left as Record<string, unknown>;
  const rightRecord = right as Record<string, unknown>;
  const leftKeys = Object.keys(leftRecord).sort();
  const rightKeys = Object.keys(rightRecord).sort();
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] &&
        engineeringValuesEqual(leftRecord[key], rightRecord[key]),
    )
  );
}
